import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SegmentCategory } from '../../types/segment-category.type';
import { CreateSegmentRequestDTO } from '../../types/dtos/create-segment.dto';
import { SEGMENT_CATEGORIES, SEGMENT_CATEGORY_LABELS } from '../../constants/segment-category.constants';
import { SEGMENT_RATE_ERROR, SEGMENT_RATE_MAX, isSegmentRateValid } from '../../utils/segment-rate.util';
import { SEGMENT_NAME_ERROR, SEGMENT_NAME_MAX_LENGTH, isSegmentNameValid } from '../../utils/segment-name.util';

const NAME_INPUT_ID = 'create-segment-name';
const CATEGORY_INPUT_ID = 'create-segment-category';
const DEFAULT_CATEGORY: SegmentCategory = SEGMENT_CATEGORIES[0];
const DEFAULT_RATE = '5';
const RATE_INPUT_ID = 'create-segment-rate';
const DESCRIPTION_INPUT_ID = 'create-segment-description';

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateSegmentRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateSegmentDialog({ open, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [name, setName] = useState('');
  const [category, setCategory] = useState<SegmentCategory>(DEFAULT_CATEGORY);
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;

    setName('');
    setCategory(DEFAULT_CATEGORY);
    setRate(DEFAULT_RATE);
    setDescription('');
    setIsActive(true);
  }, [open]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const isNameInvalid = !isSegmentNameValid(name);
  const isRateInvalid = !isSegmentRateValid(rate);
  const canSubmit = !isNameInvalid && !isRateInvalid && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    onSubmit({
      name: trimmedName,
      category,
      amount: Number(rate),
      isActive,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isSubmitting} fullWidth maxWidth="sm">
      <DialogTitle>Créer un segment</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={NAME_INPUT_ID}
            label="Nom"
            inputProps={{ maxLength: SEGMENT_NAME_MAX_LENGTH }}
            error={name !== '' && isNameInvalid}
            helperText={name !== '' && isNameInvalid ? SEGMENT_NAME_ERROR : undefined}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <div className={classes.row}>
            <FormControl variant="outlined" size="small" required>
              <InputLabel htmlFor={CATEGORY_INPUT_ID}>Catégorie</InputLabel>
              <Select
                native
                label="Catégorie"
                value={category}
                inputProps={{ id: CATEGORY_INPUT_ID }}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setCategory(event.target.value as SegmentCategory)
                }
              >
                {SEGMENT_CATEGORIES.map((segmentCategory) => (
                  <option key={segmentCategory} value={segmentCategory}>
                    {SEGMENT_CATEGORY_LABELS[segmentCategory]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              variant="outlined"
              required
              id={RATE_INPUT_ID}
              label="Taux"
              type="number"
              inputProps={{ min: 0, max: SEGMENT_RATE_MAX, step: 0.01 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              error={rate !== '' && isRateInvalid}
              helperText={rate !== '' && isRateInvalid ? SEGMENT_RATE_ERROR : undefined}
              value={rate}
              onChange={(event) => setRate(event.target.value)}
            />
          </div>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            id={DESCRIPTION_INPUT_ID}
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="default"
                classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label="Actif"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
